// YouTube embed + tab-audio capture for the Studio's "From link" tab.
//
// The whole pipeline lives in the page: YouTube serves the video to an
// official IFrame embed (the user's own IP and session — nothing for YouTube
// to rate-limit or block), we capture THIS tab's audio via getDisplayMedia
// while the embed plays, and the recorded blob drops into the exact same
// flow as an uploaded file. No backend involvement at any step.
//
// Why not fetch the audio directly: googlevideo/innertube endpoints send no
// CORS headers, so a web page can neither call them nor read their bytes —
// and stream URLs are IP-locked, so a server-resolved URL is useless to the
// browser anyway. Capturing the tab's own playback is the one path that
// works from an ordinary page.

/* ── minimal IFrame API surface ─────────────────────────────────────── */

export interface YtPlayer {
  getCurrentTime(): number;
  getDuration(): number;
  getPlayerState(): number;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  playVideo(): void;
  pauseVideo(): void;
  mute(): void;
  unMute(): void;
  destroy(): void;
}

export const YT_STATE = {
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  CUED: 5,
} as const;

interface YtNamespace {
  Player: new (
    el: HTMLElement,
    opts: {
      videoId: string;
      width?: string | number;
      height?: string | number;
      playerVars?: Record<string, string | number>;
      events?: {
        onReady?: (e: { target: YtPlayer }) => void;
        onError?: (e: { data: number }) => void;
        onStateChange?: (e: { data: number }) => void;
      };
    },
  ) => YtPlayer;
}

declare global {
  interface Window {
    YT?: YtNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

let ytApiPromise: Promise<YtNamespace> | null = null;

/** Inject https://www.youtube.com/iframe_api once and resolve with the YT namespace. */
export function loadYouTubeAPI(): Promise<YtNamespace> {
  if (ytApiPromise) return ytApiPromise;
  ytApiPromise = new Promise<YtNamespace>((resolve, reject) => {
    if (window.YT?.Player) {
      resolve(window.YT);
      return;
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev?.();
      resolve(window.YT!);
    };
    const s = document.createElement('script');
    s.src = 'https://www.youtube.com/iframe_api';
    s.onerror = () => {
      ytApiPromise = null;
      reject(new Error('yt-api-load-failed'));
    };
    document.head.appendChild(s);
  });
  return ytApiPromise;
}

/** Video id from any pasteable YouTube URL form (watch/shorts/embed/youtu.be). */
export function parseYtVideoId(raw: string): string | null {
  try {
    const u = new URL(/^https?:/i.test(raw) ? raw : 'https://' + raw);
    if (/(^|\.)youtu\.be$/i.test(u.hostname)) {
      const id = u.pathname.split('/').filter(Boolean)[0];
      return id && /^[\w-]{6,}$/.test(id) ? id : null;
    }
    if (!/(^|\.)youtube\.com$/i.test(u.hostname)) return null;
    const v = u.searchParams.get('v');
    if (v && /^[\w-]{6,}$/.test(v)) return v;
    const m = u.pathname.match(/\/(?:shorts|embed)\/([\w-]{6,})/);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

/** Player onError codes → user-facing copy. */
export function embedErrorMessage(code: number): string {
  if (code === 101 || code === 150)
    return 'This channel doesn’t allow its videos to play outside YouTube. Try another link — or use the Riffloop extension right on the video page.';
  if (code === 100) return 'This video is unavailable — it may be private or removed.';
  return 'This video can’t be played here. Check the link and try again.';
}

/**
 * Re-exported so this module keeps its existing import surface; the flags and
 * the answer now live in one place (lib/deviceTier), because the splitter
 * funnel needs the same question answered and was previously guessing.
 */
export { isMobileTier } from './deviceTier';

export function tabCaptureSupported(): boolean {
  // Some mobile browsers expose getDisplayMedia but cannot actually capture tab
  // audio, so the feature-detect below passes and the capture flow renders into
  // a dead end (a blank area under the video on iPhone). /vendor/mobile-tier.js
  // sets this flag on phones; desktop never sets it and falls through.
  if (typeof window !== 'undefined' && window.__RL_NO_CAPTURE__) return false;
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.mediaDevices?.getDisplayMedia &&
    typeof MediaRecorder !== 'undefined'
  );
}

/* ── capture engine ─────────────────────────────────────────────────── */

export type CapturePhase =
  | 'permission' // share dialog is up
  | 'ad-wait' // playing, but the content clock hasn't started (pre-roll ad)
  | 'recording'
  | 'paused' // content clock frozen (mid-roll ad, buffering, or user pause) — recorder paused too
  | 'finishing';

export type CaptureFailCode = 'denied' | 'no-audio' | 'share-ended' | 'too-short' | 'timeout' | 'recorder';

export class CaptureError extends Error {
  constructor(
    public code: CaptureFailCode,
    message: string,
  ) {
    super(message);
  }
}

export function captureErrorMessage(e: CaptureError): string | null {
  switch (e.code) {
    case 'denied':
      return null; // user closed the share dialog — not an error worth showing
    case 'no-audio':
      return 'The tab was shared without its audio. Start again and turn on “Also share tab audio” in the dialog (Chrome or Edge on desktop).';
    case 'share-ended':
      return 'Tab sharing was stopped before anything was captured. Start again and leave sharing on until the section finishes.';
    case 'too-short':
      return 'The capture was too short to use. Let the song play for at least a couple of seconds.';
    case 'timeout':
      return 'The video never started playing. Press play on the video and try again.';
    default:
      return 'Recording failed. Try again.';
  }
}

export interface CaptureHandle {
  /** Resolves with the recorded blob, or null if cancelled. Rejects with CaptureError. */
  done: Promise<Blob | null>;
  /** Finish early and keep what was captured so far. */
  stop(): void;
  /** Abandon the capture and discard everything. */
  cancel(): void;
}

const TICK_MS = 120;
const FROZEN_MS = 1000; // content clock still this long → pause the recorder
const ARM_TIMEOUT_MS = 90_000; // give up if the content never starts (long ad chain / stuck player)

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Record the tab's audio while `player` plays [start, end].
 *
 * The recorder is keyed to the CONTENT clock (player.getCurrentTime), not
 * wall time: it only starts once the clock advances (skips pre-roll ads) and
 * pauses whenever the clock freezes (mid-roll ads, buffering, user pause) —
 * so ad audio never lands in the capture.
 */
export function startCapture(
  player: YtPlayer,
  range: { start: number; end: number },
  onPhase: (phase: CapturePhase) => void,
  onProgress: (capturedSec: number) => void,
): CaptureHandle {
  let stopRequested = false;
  let cancelRequested = false;
  let shareEnded = false;

  const done = (async (): Promise<Blob | null> => {
    onPhase('permission');
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        // Chromium-only picker hints (absent from the TS lib): preselect this
        // tab and keep it offered, so sharing is a single click.
        ...({ preferCurrentTab: true, selfBrowserSurface: 'include', systemAudio: 'exclude' } as object),
      } as MediaStreamConstraints);
    } catch {
      throw new CaptureError('denied', 'share dialog dismissed');
    }

    const cleanupStream = () => stream.getTracks().forEach((t) => t.stop());
    const audioTracks = stream.getAudioTracks();
    if (!audioTracks.length) {
      cleanupStream();
      throw new CaptureError('no-audio', 'no tab audio shared');
    }
    audioTracks[0].addEventListener('ended', () => {
      shareEnded = true;
    });

    const audioStream = new MediaStream(audioTracks);
    let rec: MediaRecorder;
    try {
      rec = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? new MediaRecorder(audioStream, { mimeType: 'audio/webm;codecs=opus', audioBitsPerSecond: 192_000 })
        : new MediaRecorder(audioStream);
    } catch {
      cleanupStream();
      throw new CaptureError('recorder', 'MediaRecorder init failed');
    }
    const chunks: BlobPart[] = [];
    rec.ondataavailable = (e) => {
      if (e.data.size) chunks.push(e.data);
    };
    const recStopped = new Promise<void>((res) => {
      rec.onstop = () => res();
    });

    const finalize = async (capturedSec: number): Promise<Blob | null> => {
      onPhase('finishing');
      if (rec.state !== 'inactive') rec.stop();
      await recStopped;
      cleanupStream();
      try {
        player.pauseVideo();
      } catch {
        /* player may already be gone */
      }
      if (cancelRequested) return null;
      if (capturedSec < 1) throw new CaptureError('too-short', 'nothing usable captured');
      return new Blob(chunks, { type: rec.mimeType || 'audio/webm' });
    };

    // Roll the player. If a pre-roll ad eats the seek, we re-seek once the
    // content clock comes alive.
    player.seekTo(range.start, true);
    player.playVideo();

    let lastT = player.getCurrentTime();
    let frozenMs = 0;
    let armedMs = 0;
    let recording = false;
    let capturedSec = 0;

    for (;;) {
      await sleep(TICK_MS);
      if (cancelRequested) return finalize(0);
      if (shareEnded) {
        if (capturedSec >= 3) return finalize(capturedSec); // keep a usable partial
        await finalize(0).catch(() => {});
        throw new CaptureError('share-ended', 'sharing stopped');
      }

      const t = player.getCurrentTime();
      const state = player.getPlayerState();
      const advanced = t - lastT > 0.02;
      lastT = t;

      if (!recording) {
        armedMs += TICK_MS;
        if (advanced && t >= range.start - 1.5) {
          if (t < range.start - 0.5) {
            // Content is rolling but the ad reset our position — re-seek once.
            player.seekTo(range.start, true);
            continue;
          }
          rec.start();
          recording = true;
          onPhase('recording');
        } else {
          if (advanced && t < range.start - 1.5) {
            player.seekTo(range.start, true);
          } else if (armedMs > 1500) {
            onPhase('ad-wait');
          }
          if (armedMs > ARM_TIMEOUT_MS) {
            await finalize(0).catch(() => {});
            throw new CaptureError('timeout', 'content never started');
          }
        }
        continue;
      }

      // Recording: follow the content clock.
      if (advanced) {
        frozenMs = 0;
        if (rec.state === 'paused') {
          rec.resume();
          onPhase('recording');
        }
        capturedSec = Math.max(capturedSec, Math.min(t, range.end) - range.start);
        onProgress(capturedSec);
      } else {
        frozenMs += TICK_MS;
        if (frozenMs >= FROZEN_MS && rec.state === 'recording') {
          rec.pause(); // mid-roll ad / buffering / user pause — keep it out of the take
          onPhase('paused');
        }
      }

      if (stopRequested || t >= range.end - 0.05 || state === YT_STATE.ENDED) {
        return finalize(capturedSec);
      }
    }
  })();

  return {
    done,
    stop: () => {
      stopRequested = true;
    },
    cancel: () => {
      cancelRequested = true;
    },
  };
}
